/**
 * Haptic Manager — Lightweight vibration feedback using the Vibration API.
 *
 * Provides tactile feedback for UI interactions on supported devices (mostly mobile).
 * Reads the `haptic` preference from dataLayer to respect user settings.
 * Fails gracefully on desktop or unsupported browsers.
 *
 * Usage:
 *   import { hapticManager } from '../utils/hapticManager'
 *   hapticManager.hapticLight()
 *   hapticManager.hapticSuccess()
 */

import { dataLayer } from '../data/dataLayer'

let hapticEnabled = true

// Cache the preference
async function isHapticEnabled(): Promise<boolean> {
  try {
    const prefs = await dataLayer.getLearnerPreferences()
    // Default to true if not explicitly set, but rely on browser support
    hapticEnabled = prefs.haptic !== false
    return hapticEnabled
  } catch {
    return hapticEnabled
  }
}

function vibrate(pattern: number | number[]) {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return

  isHapticEnabled()
    .then((enabled) => {
      if (enabled) {
        try {
          navigator.vibrate(pattern)
        } catch (e) {
          // Ignore errors (e.g., user hasn't interacted with document yet)
        }
      }
    })
    .catch(() => {})
}

// ── Public API ──────────────────────────────────────────────

/** 10ms vibration for UI taps, selections */
function hapticLight() {
  vibrate(10)
}

/** 25ms for confirmations, navigation */
function hapticMedium() {
  vibrate(25)
}

/** 50ms for level-ups, achievements */
function hapticHeavy() {
  vibrate(50)
}

/** 5ms micro-tap for toggles, checkboxes */
function hapticSelection() {
  vibrate(5)
}

/** pattern [10, 50, 20] for correct answers */
function hapticSuccess() {
  vibrate([10, 50, 20])
}

/** pattern [40, 30, 40] for wrong answers (not punishing) */
function hapticError() {
  vibrate([40, 30, 40])
}

/** pattern [10, 30, 10, 30, 50] for milestones, streaks */
function hapticCelebration() {
  vibrate([10, 30, 10, 30, 50])
}

/** Force-refresh the cached haptic preference (call after settings change) */
function refreshPreference() {
  isHapticEnabled()
}

export const hapticManager = {
  hapticLight,
  hapticMedium,
  hapticHeavy,
  hapticSelection,
  hapticSuccess,
  hapticError,
  hapticCelebration,
  refreshPreference,
} as const
